/**
 * Data-access layer for Settings → Practices & Users.
 *
 * Reads Organizations, org-scoped Practitioners/Patients from Medplum and maps them to directory view models.
 */

import type { MedplumClient } from '@medplum/core';
import { config } from '../config';
import { toFhirDateOnlyLocal } from './dateHelpers';
import type { Organization, Patient, Practitioner, ServiceRequest } from '@medplum/fhirtypes';
import {
  type CreateUserPayload,
  fhirReferenceString,
  organizationIdFromServiceRequestPerformer,
  organizationIdsFromPatientMeta,
  organizationToPractice,
  practitionerToUser,
  patientToUser,
} from './adminDirectoryMapping';
import { getUserRoleGroup } from '../pages/users/types/rbac';
export type { CreateUserPayload } from './adminDirectoryMapping';
import type { Practice, User } from '../pages/users/types/rbac';
import { CLIENT_ROLES } from '../pages/users/types/rbac';
import type { CreatePracticeBotInput } from './adminActionsService';
import {
  PLATFORM_ORGANIZATION_IDENTIFIER_QUERY,
} from '@unify/fhir-model/constants';
import { isUnifyPlatformOrganization } from '@unify/fhir-model/staff-role-org';

export type CreatePracticePayload = Omit<CreatePracticeBotInput, 'action'>;

// ---------------------------------------------------------------------------
// Live Medplum — query helpers
// ---------------------------------------------------------------------------

/**
 * Directory rows render live names (preferred + legal), so every fetch must reach the
 * server instead of replaying a cached search from an earlier profile edit.
 */
const NO_CACHE = { cache: 'no-cache' } as const;

async function fetchUnifyPlatformOrganization(
  medplum: MedplumClient,
): Promise<Organization | undefined> {
  const matches = await medplum.searchResources('Organization', {
    identifier: PLATFORM_ORGANIZATION_IDENTIFIER_QUERY,
    _count: '2',
  }, NO_CACHE);
  if (matches.length !== 1) return undefined;
  const org = matches[0];
  if (!org.id || !isUnifyPlatformOrganization(org)) return undefined;
  return org;
}

async function fetchOrganizations(medplum: MedplumClient): Promise<Organization[]> {
  return medplum.searchResources('Organization', {
    type: 'prov',
    _count: '200',
  }, NO_CACHE);
}

async function fetchPractitioners(medplum: MedplumClient): Promise<Practitioner[]> {
  return medplum.searchResources('Practitioner', {
    _count: '500',
  }, NO_CACHE);
}

async function fetchPatients(medplum: MedplumClient): Promise<Patient[]> {
  return medplum.searchResources('Patient', {
    _count: '500',
  }, NO_CACHE);
}

async function fetchServiceRequests(medplum: MedplumClient): Promise<ServiceRequest[]> {
  return medplum.searchResources('ServiceRequest', {
    _count: '500',
  }, NO_CACHE);
}

function buildClientIdsByOrganizationId(
  serviceRequests: ServiceRequest[],
  patients: Patient[],
): Map<string, Set<string>> {
  const map = new Map<string, Set<string>>();
  const add = (orgId: string, patientId: string): void => {
    const set = map.get(orgId) ?? new Set<string>();
    set.add(patientId);
    map.set(orgId, set);
  };

  for (const sr of serviceRequests) {
    const orgId = organizationIdFromServiceRequestPerformer(sr);
    const subjectRef = fhirReferenceString(sr.subject);
    const patientId = subjectRef?.startsWith('Patient/')
      ? subjectRef.replace(/^Patient\//, '')
      : undefined;
    if (!orgId || !patientId) continue;
    add(orgId, patientId);
  }

  for (const patient of patients) {
    if (!patient.id) continue;
    for (const orgId of organizationIdsFromPatientMeta(patient)) {
      add(orgId, patient.id);
    }
  }

  return map;
}

function buildPracticeMap(orgs: Organization[]): Map<string, { id: string; name: string }> {
  const map = new Map<string, { id: string; name: string }>();
  for (const o of orgs) {
    if (o.id) map.set(o.id, { id: o.id, name: o.name ?? '' });
  }
  return map;
}

function countUsersPerOrg(
  users: User[],
  clientIdsByOrganizationId: Map<string, Set<string>>,
): Map<string, number> {
  const counts = new Map<string, number>();
  for (const u of users) {
    if (!u.practiceId) continue;
    counts.set(u.practiceId, (counts.get(u.practiceId) ?? 0) + 1);
  }
  for (const [orgId, patientIds] of clientIdsByOrganizationId) {
    counts.set(orgId, (counts.get(orgId) ?? 0) + patientIds.size);
  }
  return counts;
}

// ---------------------------------------------------------------------------
// Public API — List
// ---------------------------------------------------------------------------

export interface AdminDirectoryData {
  practices: Practice[];
  users: User[];
  /** Client Patient ids listed under each practice (ServiceRequest.performer + Patient compartment). */
  clientIdsByOrganizationId: Record<string, string[]>;
  platformOrganization: { id: string; name: string } | null;
}

export async function fetchAdminDirectory(medplum: MedplumClient): Promise<AdminDirectoryData> {
  const [orgs, platformOrg, practitioners, patients, serviceRequests] = await Promise.all([
    fetchOrganizations(medplum),
    fetchUnifyPlatformOrganization(medplum),
    fetchPractitioners(medplum),
    fetchPatients(medplum),
    fetchServiceRequests(medplum),
  ]);

  const practiceMap = buildPracticeMap(orgs);
  if (platformOrg?.id) {
    practiceMap.set(platformOrg.id, { id: platformOrg.id, name: platformOrg.name ?? 'Unify' });
  }
  const clientIdsByOrganizationId = buildClientIdsByOrganizationId(serviceRequests, patients);

  const users: User[] = [];
  const seenUserIds = new Set<string>();
  for (const p of practitioners) {
    const u = practitionerToUser(p, practiceMap);
    if (!u?.id || seenUserIds.has(u.id)) continue;
    seenUserIds.add(u.id);
    users.push(u);
  }
  // Clients appear in practice tabs when linked via ServiceRequest.performer or Patient org compartment.
  for (const p of patients) {
    const u = patientToUser(p);
    if (!u?.id || getUserRoleGroup(u.role) !== 'clients' || seenUserIds.has(u.id)) continue;
    seenUserIds.add(u.id);
    users.push(u);
  }
  users.sort((a, b) => a.name.localeCompare(b.name));

  const userCounts = countUsersPerOrg(users, clientIdsByOrganizationId);
  const practices = orgs
    .map((o) => organizationToPractice(o, userCounts.get(o.id ?? '') ?? 0))
    .sort((a, b) => a.name.localeCompare(b.name));

  const clientIdsByOrganizationIdRecord: Record<string, string[]> = {};
  for (const [orgId, ids] of clientIdsByOrganizationId) {
    clientIdsByOrganizationIdRecord[orgId] = [...ids];
  }

  return {
    practices,
    users,
    clientIdsByOrganizationId: clientIdsByOrganizationIdRecord,
    platformOrganization: platformOrg?.id
      ? { id: platformOrg.id, name: platformOrg.name ?? 'Unify' }
      : null,
  };
}

/** Users visible on a practice's Practitioners / Clients tabs. */
export function filterUsersForPractice(
  users: User[],
  practiceId: string,
  clientIdsByOrganizationId: Record<string, string[]>,
): User[] {
  const orgClientIds = new Set(clientIdsByOrganizationId[practiceId] ?? []);
  return users.filter((u) => {
    if (getUserRoleGroup(u.role) === 'clients') {
      return orgClientIds.has(u.id);
    }
    return u.practiceId === practiceId;
  });
}

// ---------------------------------------------------------------------------
// Public API — Create Practice (delegates to unify-admin-actions bot)
// ---------------------------------------------------------------------------

export async function createPracticeLive(
  medplum: MedplumClient,
  payload: CreatePracticePayload,
): Promise<Practice> {
  const { createPractice: createPracticeBotAction } = await import('./adminActionsService');

  const { defaultRolePermissions } = await import('../pages/users/types/permissions');
  const uiPermissions = JSON.stringify(defaultRolePermissions['practice-owner'] ?? {});

  const projectId = config.medplum.projectId;
  if (!projectId) {
    throw new Error(
      'Medplum project ID is required to create a practice. Set VITE_MEDPLUM_PROJECT_ID in medplum-ui/.env (see README), then restart the dev server.',
    );
  }

  const result = await createPracticeBotAction(medplum, {
    practice: payload.practice,
    owner: payload.owner,
    projectId,
    uiPermissions,
  });

  const p = payload.practice;
  const addr = p.address;
  const hasAddr = addr && (addr.line?.length || addr.city || addr.state || addr.postalCode);

  return {
    id: result.organization.id,
    name: result.organization.name,
    ownerId: result.owner.practitionerId,
    ownerName: `${payload.owner.firstName ?? ''} ${payload.owner.lastName ?? payload.owner.name ?? ''}`.trim(),
    userCount: 1,
    createdAt: toFhirDateOnlyLocal(new Date()),
    status: 'Active',
    ...(p.legalName ? { legalName: p.legalName } : {}),
    ...(p.contactEmail ? { email: p.contactEmail } : {}),
    ...(p.phone ? { phone: p.phone } : {}),
    ...(p.ein ? { taxId: p.ein } : {}),
    ...(p.npi ? { npi: p.npi } : {}),
    ...(hasAddr
      ? {
          legalEntityAddress: {
            addressLine1: addr.line?.[0],
            addressLine2: addr.line?.[1],
            city: addr.city,
            state: addr.state,
            zipCode: addr.postalCode,
          },
        }
      : {}),
  };
}

// ---------------------------------------------------------------------------
// Public API — Create User (Provider / Staff / Client)
//
// Uses the practice-admin-actions bot invite-user flow, which delegates to
// Medplum's invite endpoint for user/profile/membership creation.
// ---------------------------------------------------------------------------

export async function createUserLive(
  medplum: MedplumClient,
  payload: CreateUserPayload,
): Promise<User> {
  const isClient = CLIENT_ROLES.includes(payload.role);
  const { inviteUser } = await import('./adminActionsService');
  const { defaultRolePermissions } = await import('../pages/users/types/permissions');

  const resourceType = isClient ? 'Patient' as const : 'Practitioner' as const;

  const firstName = payload.firstName.trim();
  const lastName = payload.lastName.trim();

  const uiPermissions = JSON.stringify(defaultRolePermissions[payload.role] ?? {});

  const projectId = config.medplum.projectId;
  if (!projectId) {
    throw new Error(
      'Medplum project ID is required to invite users. Set VITE_MEDPLUM_PROJECT_ID in medplum-ui/.env (see README), then restart the dev server.',
    );
  }

  const normalizedEmail = payload.email.trim().toLowerCase();

  const additionalRoles = (payload.additionalRoles ?? []).filter((r) => r && r !== payload.role);

  const result = await inviteUser(medplum, {
    firstName,
    lastName,
    email: normalizedEmail,
    role: payload.role,
    resourceType,
    orgId: payload.practiceId || undefined,
    // AccessPolicy is resolved in practice-admin-actions invite-user by role + resourceType.
    // invite-user always invites with sendEmail:false; branded Welcome mail is delivered by the
    // UserSecurityRequest Subscription on the email-sender bot.
    uiPermissions,
    projectId,
    ...(additionalRoles.length ? { additionalRoles } : {}),
  });

  return {
    id: result.profileId,
    name: `${firstName} ${lastName}`.trim(),
    email: normalizedEmail,
    role: payload.role,
    practiceId: isClient ? '' : payload.practiceId,
    practiceName: isClient ? '' : payload.practiceName,
    status: 'Pending',
    createdAt: toFhirDateOnlyLocal(new Date()),
    lastActive: '—',
  };
}

export async function resendInvite(medplum: MedplumClient, email: string): Promise<void> {
  const { resendInvite: resendInviteAction } = await import('./adminActionsService');
  const normalizedEmail = email.trim().toLowerCase();
  await resendInviteAction(medplum, {
    email: normalizedEmail,
    ...(config.medplum.projectId ? { projectId: config.medplum.projectId } : {}),
  });
}
